import { API_URL, fetchWithHandling } from './api';

/**
 * The two human-in-the-loop queues from the meeting pipeline.
 *
 * Proposals: follow-ups the tracker would like somebody to send. Approving one
 * records a decision and sends nothing — the backend has no delivery path, and
 * ProposalStatus has no SENT member. The UI says so rather than implying a
 * message went out.
 *
 * Clarifications: questions the pipeline could not answer for itself. Answering
 * one is recorded against the meeting so a reprocess never re-asks it, and a
 * TERM or ALIAS answer is promoted into the project glossary as HUMAN_CONFIRMED.
 *
 * All of these need the project-scoped token (localStorage `projectToken`,
 * exposed as `userGoogleId` by DataProvider), not the account token.
 */

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

const jsonHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...authHeaders(token),
});

export const listAttentionReviews = (
  token,
  { sourceMeetingId = null } = {},
) => {
  const params = new URLSearchParams();
  if (sourceMeetingId) params.set('source_meeting_id', sourceMeetingId);
  return fetchWithHandling(
    `${API_URL}/api/project-register/attention-reviews?${params}`,
    {
      method: 'GET',
      headers: authHeaders(token),
    },
  );
};

export const reviewAttentionEntity = (token, entity, decision) =>
  fetchWithHandling(
    `${API_URL}/api/project-register/attention-reviews/${encodeURIComponent(entity.collection)}/${encodeURIComponent(entity.entity_id)}`,
    {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify({ decision }),
    },
  );

// --- Follow-up proposals ---------------------------------------------------

export const listProposals = async (
  token,
  { status = 'PENDING_APPROVAL', sourceMeetingId = null, limit = 50 } = {},
) => {
  const params = new URLSearchParams({ status, limit: String(limit) });
  // Scopes the queue to one meeting, so a meeting screen shows its own
  // follow-ups rather than the whole project's.
  if (sourceMeetingId) params.set('source_meeting_id', sourceMeetingId);
  return fetchWithHandling(`${API_URL}/api/meetings/proposals?${params}`, {
    method: 'GET',
    headers: authHeaders(token),
  });
};

export const approveProposal = async (token, proposalId, note = null) =>
  fetchWithHandling(`${API_URL}/api/meetings/proposals/${proposalId}/approve`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({ note }),
  });

export const rejectProposal = async (token, proposalId, note = null) =>
  fetchWithHandling(`${API_URL}/api/meetings/proposals/${proposalId}/reject`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({ note }),
  });

export const recordProposalDelivery = (token, proposalId, reference) =>
  fetchWithHandling(
    `${API_URL}/api/meetings/proposals/${encodeURIComponent(proposalId)}/delivery`,
    {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify({ reference }),
    },
  );

// --- Clarification questions ----------------------------------------------

export const listClarifications = async (
  token,
  { sourceMeetingId = null, limit = 50 } = {},
) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (sourceMeetingId) params.set('source_meeting_id', sourceMeetingId);
  return fetchWithHandling(`${API_URL}/api/meetings/clarifications?${params}`, {
    method: 'GET',
    headers: authHeaders(token),
  });
};

export const answerClarification = async (token, clarificationId, answer) =>
  fetchWithHandling(
    `${API_URL}/api/meetings/clarifications/${clarificationId}/answer`,
    {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify({
        resolution: answer.resolution,
        resolved_entity_ref: answer.resolvedEntityRef || null,
        resolved_by_person_id: answer.resolvedByPersonId,
      }),
    },
  );

// --- Commitments / concerns review gate -------------------------------------
//
// Commitments and concerns commit straight to the live register with no
// gate (see stage_five/service.py._prepare) -- these calls are an additive
// review overlay: `pending_review` on a just-committed candidate, cleared
// by approve/reject. Approving/rejecting does not undo the commit -- the
// register tabs (Risks/Dependencies/Insights) already show the item either
// way. Uses the same project-register endpoints those tabs read from.

export const listPendingCommitments = async (token, sourceMeetingId) =>
  fetchWithHandling(
    `${API_URL}/api/project-register/commitments?${new URLSearchParams({
      source_meeting_id: sourceMeetingId,
      pending_review: 'true',
      limit: '50',
    })}`,
    { method: 'GET', headers: authHeaders(token) },
  );

export const reviewCommitment = async (token, entityId, decision) =>
  fetchWithHandling(
    `${API_URL}/api/project-register/commitments/${entityId}/${decision === 'ACCEPTED' ? 'approve' : 'reject'}`,
    { method: 'POST', headers: authHeaders(token) },
  );

export const listPendingConcerns = async (token, sourceMeetingId) =>
  fetchWithHandling(
    `${API_URL}/api/project-register/concerns?${new URLSearchParams({
      source_meeting_id: sourceMeetingId,
      pending_review: 'true',
      limit: '50',
    })}`,
    { method: 'GET', headers: authHeaders(token) },
  );

export const reviewConcern = async (token, entityId, decision) =>
  fetchWithHandling(
    `${API_URL}/api/project-register/concerns/${entityId}/${decision === 'ACCEPTED' ? 'approve' : 'reject'}`,
    { method: 'POST', headers: authHeaders(token) },
  );
